import React, { useEffect, useMemo, useRef, useState } from 'react';
import { copyToClipboard, plural, rowHeightFor, useAdmin, useDebounced, useDensity } from '../context';
import { PanelMode, PanelPlayer } from '../types';
import { Icon } from '../icons';
import { Empty, Segmented, ViewHeader, VirtualList } from '../ui';

type Filter = 'all' | 'online' | 'offline' | 'banned';
type Sort = 'online' | 'name' | 'profile' | 'ping';

const ROW_HEIGHT = 40;

const PlayerRow = React.memo(function PlayerRow({ p, selected }: { p: PanelPlayer; selected: boolean }) {
  return (
    <div className={'admin-panel__prow' + (selected ? ' admin-panel__prow--selected' : '') + (p.online ? '' : ' admin-panel__prow--offline')}>
      <span className={'admin-panel__dot' + (p.online ? ' admin-panel__dot--online' : '')} title={p.online ? 'En ligne' : 'Hors ligne'} />
      <span className="admin-panel__cell admin-panel__cell--name">
        {p.n || '-'}
        {p.banned ? <span className="admin-panel__tag admin-panel__tag--danger">Banni</span> : null}
      </span>
      <span className="admin-panel__cell admin-panel__cell--muted">{p.p}</span>
      <span className="admin-panel__cell admin-panel__cell--muted" title={p.prof}>{p.prof || '-'}</span>
      <span className="admin-panel__cell admin-panel__cell--muted admin-panel__num">{p.ping != null ? p.ping + ' ms' : '-'}</span>
    </div>
  );
});

const SORTS: Array<{ id: Sort; label: string }> = [
  { id: 'online', label: 'En ligne d\'abord' },
  { id: 'name', label: 'Nom' },
  { id: 'profile', label: 'Numéro de profil' },
  { id: 'ping', label: 'Ping' },
];

const byName = (a: PanelPlayer, b: PanelPlayer): number => (a.n || '').localeCompare(b.n || '', 'fr', { sensitivity: 'base' });

interface Props {
  players: PanelPlayer[];
  modes: PanelMode[];
  selected: number | null;
  onSelect: (profileId: number | null) => void;
}

const PlayersViewInner: React.FC<Props> = ({ players, modes, selected, onSelect }) => {
  const { can } = useAdmin();
  const density = useDensity();
  const tableRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [sort, setSort] = useState<Sort>('online');
  const [cursorKey, setCursorKey] = useState<number | null>(null);
  const seeNetwork = can('ban');
  const dSearch = useDebounced(search, 120);

  const counts = useMemo(() => {
    let online = 0;
    let banned = 0;
    for (const p of players) {
      if (p.online) online++;
      if (p.banned) banned++;
    }
    return { all: players.length, online, offline: players.length - online, banned };
  }, [players]);

  const shown = useMemo(() => {
    const q = dSearch.trim().toLowerCase();
    const list = players.filter((p) => {
      if (filter === 'online' && !p.online) return false;
      if (filter === 'offline' && p.online) return false;
      if (filter === 'banned' && !p.banned) return false;
      if (!q) return true;
      const hay = [p.n, p.d, String(p.p), p.a || '', p.prof, seeNetwork ? p.ip : '', seeNetwork ? p.hwid : ''].join(' ').toLowerCase();
      return hay.indexOf(q) !== -1;
    });
    list.sort((a, b) => {
      if (sort === 'profile') return a.p - b.p;
      if (sort === 'ping') {
        const pa = a.ping == null ? Infinity : a.ping;
        const pb = b.ping == null ? Infinity : b.ping;
        return pa - pb || byName(a, b);
      }
      if (sort === 'online') return Number(b.online) - Number(a.online) || byName(a, b);
      return byName(a, b);
    });
    return list;
  }, [players, dSearch, filter, sort, seeNetwork]);

  // A selection from elsewhere (the global search, the overview) brings the cursor along
  useEffect(() => {
    if (selected != null) setCursorKey(selected);
  }, [selected]);

  const cursor = cursorKey == null ? -1 : shown.findIndex((p) => p.p === cursorKey);

  const selectedPlayer = selected == null ? null : players.find((p) => p.p === selected) || null;

  // Closing the sheet gives the focus back to the list, so the arrows keep working
  const closeDrawer = () => {
    onSelect(null);
    window.setTimeout(() => {
      const el = tableRef.current ? (tableRef.current.querySelector('[role="listbox"]') as HTMLElement | null) : null;
      if (el) el.focus();
    }, 0);
  };
  const { onEscape } = useAdmin();
  useEffect(() => {
    if (!selectedPlayer) return undefined;
    return onEscape(closeDrawer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!selectedPlayer]);

  return (
    <div className={'admin-panel__view admin-panel__players' + (selectedPlayer ? ' admin-panel__players--drawer' : '')}>
      <div className="admin-panel__players-main">
        <ViewHeader title="Joueurs" subtitle={`${counts.online} en ligne sur ${counts.all} ${plural(counts.all, 'personnage connu', 'personnages connus')}`} />
        <div className="admin-panel__toolbar">
          <input
            className="admin-panel__input admin-panel__input--grow"
            type="search"
            placeholder="Nom, Discord ID, numéro de profil, métier"
            aria-label="Rechercher un joueur"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Segmented<Filter>
            label="Filtrer les joueurs"
            value={filter}
            onChange={setFilter}
            options={[
              { id: 'all', label: 'Tous', count: counts.all },
              { id: 'online', label: 'En ligne', count: counts.online },
              { id: 'offline', label: 'Hors ligne', count: counts.offline },
              ...(counts.banned || filter === 'banned' ? [{ id: 'banned' as Filter, label: 'Bannis', count: counts.banned }] : []),
            ]}
          />
          <label className="admin-panel__select-wrap">
            <span className="admin-panel__select-label">Tri</span>
            <select className="admin-panel__select" value={sort} onChange={(e) => setSort(e.target.value as Sort)}>
              {SORTS.map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="admin-panel__table" ref={tableRef}>
          <div className="admin-panel__prow admin-panel__prow--head" aria-hidden="true">
            <span />
            <span>Personnage</span>
            <span>Profil</span>
            <span>Métier</span>
            <span className="admin-panel__num">Ping</span>
          </div>
          <VirtualList
            className="admin-panel__table-body"
            items={shown}
            rowHeight={rowHeightFor(density, ROW_HEIGHT)}
            getKey={(p) => p.p}
            cursor={cursor}
            onCursorChange={(i) => setCursorKey(shown[i] ? shown[i].p : null)}
            onActivate={(p) => onSelect(p.p)}
            ariaLabel="Liste des joueurs"
            idPrefix="ap-player"
            empty={<Empty title="Aucun joueur trouvé" text="Changez le filtre ou la recherche." />}
            renderRow={(p) => <PlayerRow p={p} selected={p.p === selected} />}
          />
        </div>
        <p className="admin-panel__foot-hint">Flèches pour parcourir, Entrée pour ouvrir la fiche, Échap pour la fermer.</p>
      </div>

      {selectedPlayer ? (
        <PlayerDrawer key={selectedPlayer.p} player={selectedPlayer} modes={modes} onClose={closeDrawer} seeNetwork={seeNetwork} />
      ) : null}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Side drawer: identity and every action on one character, grouped by theme
// ---------------------------------------------------------------------------

const GOLD_QUICK = [100, 1000, 10000];
const GOLD_MAX = 10000000;

const PlayerDrawer: React.FC<{ player: PanelPlayer; modes: PanelMode[]; onClose: () => void; seeNetwork: boolean }> = ({ player, modes, onClose, seeNetwork }) => {
  const { can, act, confirm, toast } = useAdmin();
  const [gold, setGold] = useState('1000');
  const live = player.online && !!player.a;
  const target = player.a || '';
  const goldAmount = Math.floor(Number(gold));
  const goldOk = goldAmount >= 1 && goldAmount <= GOLD_MAX;

  const doCopy = (label: string, value: string) => {
    if (!value) return;
    const ok = copyToClipboard(value);
    toast(ok ? `${label} copié.` : 'Copie impossible.', { kind: ok ? 'ok' : 'err' });
  };

  const offlineHint = live ? undefined : player.online ? 'Identifiant de jeu inconnu' : 'Le joueur doit être en ligne';

  const btn = (label: string, action: string, extra: Record<string, unknown> = {}, opts: { danger?: boolean; needsLive?: boolean; wide?: boolean; pending?: string } = {}) => {
    const needsLive = opts.needsLive !== false;
    return (
      <button
        key={label}
        type="button"
        className={'admin-panel__button' + (opts.danger ? ' admin-panel__button--danger' : '')}
        disabled={needsLive && !live}
        title={needsLive && !live ? offlineHint : undefined}
        onClick={() => act(action, { target, ...extra }, opts.pending || label)}
      >
        {label}
      </button>
    );
  };

  const canPlayers = can('players');
  const canMove = can('players');
  const canMod = can('kick') || can('ban');
  const canEco = can('items');
  const canModes = can('modes');
  const modeList = modes || [];

  return (
    <aside className="admin-panel__drawer" aria-label={`Fiche de ${player.n || 'joueur'}`}>
      <div className="admin-panel__drawer-head">
        <div className="admin-panel__drawer-id">
          <div className="admin-panel__drawer-name">
            <span className={'admin-panel__dot' + (player.online ? ' admin-panel__dot--online' : '')} />
            {player.n || '(sans nom)'}
          </div>
          <div className="admin-panel__drawer-state">
            {player.online ? 'En ligne' : 'Hors ligne'}
            {player.banned ? <span className="admin-panel__tag admin-panel__tag--danger">Banni</span> : null}
          </div>
        </div>
        <button type="button" className="admin-panel__button admin-panel__button--secondary" onClick={onClose} aria-label="Fermer la fiche du joueur">
          Fermer
        </button>
      </div>

      <div className="admin-panel__drawer-scroll">
        <section className="admin-panel__group" aria-label="Identité">
          <h3 className="admin-panel__group-title">Identité</h3>
          <dl className="admin-panel__dl">
            <dt>Profil</dt>
            <dd>{player.p}</dd>
            <dt>Form ID</dt>
            <dd>{player.a ? '0x' + player.a : '-'}</dd>
            <dt>Discord ID</dt>
            <dd className="admin-panel__copyline">
              <span>{player.d || '-'}</span>
              {player.d ? (
                <button type="button" className="admin-panel__copybtn" aria-label="Copier le Discord ID" title="Copier le Discord ID" onClick={() => doCopy('Discord ID', player.d)}>
                  <Icon name="copy" size={14} />
                </button>
              ) : null}
            </dd>
            <dt>Ping</dt>
            <dd>{player.ping != null ? player.ping + ' ms' : '-'}</dd>
            <dt>Métier</dt>
            <dd>{player.prof || '-'}</dd>
            <dt>2e personnage</dt>
            <dd>{player.slot2 ? 'Emplacement accordé' : 'Non accordé'}</dd>
            {seeNetwork ? (
              <>
                <dt>Adresse IP</dt>
                <dd>{player.ip || '-'}</dd>
                <dt>HWID</dt>
                <dd className="admin-panel__wrap">{player.hwid || '-'}</dd>
              </>
            ) : null}
          </dl>
        </section>

        {canPlayers ? (
          <section className="admin-panel__group" aria-label="Secours">
            <h3 className="admin-panel__group-title">Secours</h3>
            <div className="admin-panel__btnrow">
              {btn('Soigner', 'heal')}
              {btn('Réanimer', 'revive')}
              {btn('Geler ou dégeler', 'freeze')}
              <button
                type="button"
                className="admin-panel__button"
                onClick={() => act('reviveZone', { target: live ? target : '' }, 'Zone de réanimation')}
                title="Réanime tous les joueurs à terre autour de la cible, ou de vous"
              >
                Réanimer la zone
              </button>
            </div>
          </section>
        ) : null}

        {canMove ? (
          <section className="admin-panel__group" aria-label="Déplacement">
            <h3 className="admin-panel__group-title">Déplacement</h3>
            <div className="admin-panel__btnrow">
              {btn('Me téléporter à lui', 'teleportTo')}
              {btn('L\'invoquer ici', 'summon')}
            </div>
          </section>
        ) : null}

        {canMod ? (
          <section className="admin-panel__group" aria-label="Modération">
            <h3 className="admin-panel__group-title">Modération</h3>
            <div className="admin-panel__btnrow">
              {can('kick') ? (
                <button
                  type="button"
                  className="admin-panel__button admin-panel__button--danger"
                  disabled={!live}
                  title={offlineHint}
                  onClick={() =>
                    confirm({
                      title: `Expulser ${player.n || 'ce joueur'} ?`,
                      body: 'Le joueur est déconnecté du serveur. Il peut se reconnecter aussitôt.',
                      confirmLabel: 'Expulser',
                      danger: true,
                      onConfirm: () => act('kick', { target }, 'Expulsion'),
                    })
                  }
                >
                  Expulser
                </button>
              ) : null}
              {can('ban') ? (
                <button
                  type="button"
                  className="admin-panel__button admin-panel__button--danger"
                  disabled={!live}
                  title={offlineHint}
                  onClick={() =>
                    confirm({
                      title: `Bannir ${player.n || 'ce joueur'} ?`,
                      body: `Le profil ${player.p} est ajouté à la liste des bannis et ne pourra plus se connecter tant qu'il n'est pas débanni.`,
                      confirmLabel: 'Bannir',
                      danger: true,
                      onConfirm: () => act('ban', { target }, 'Bannissement'),
                    })
                  }
                >
                  Bannir
                </button>
              ) : null}
              {can('ban') ? (
                <button
                  type="button"
                  className="admin-panel__button"
                  disabled={!player.banned}
                  title={player.banned ? undefined : "Ce profil n'est pas banni"}
                  onClick={() => act('unban', { target: String(player.p) }, 'Débannissement')}
                >
                  Débannir
                </button>
              ) : null}
            </div>
          </section>
        ) : null}

        {canEco ? (
          <section className="admin-panel__group" aria-label="Économie">
            <h3 className="admin-panel__group-title">Économie</h3>
            <div className="admin-panel__btnrow">
              {btn('Donner des objets', 'openItemGiverFor')}
            </div>
            <div className="admin-panel__gold">
              <label className="admin-panel__gold-label" htmlFor="ap-gold">Septims à donner</label>
              <div className="admin-panel__gold-row">
                <input
                  id="ap-gold"
                  className="admin-panel__input"
                  type="number"
                  min={1}
                  max={GOLD_MAX}
                  value={gold}
                  onChange={(e) => setGold(e.target.value)}
                />
                <button
                  type="button"
                  className="admin-panel__button admin-panel__button--primary"
                  disabled={!live || !goldOk}
                  title={!live ? offlineHint : !goldOk ? `Entre 1 et ${GOLD_MAX} Septims` : undefined}
                  onClick={() => act('giveGold', { target, amount: goldAmount }, 'Don de Septims')}
                >
                  Donner
                </button>
              </div>
              <div className="admin-panel__chips">
                {GOLD_QUICK.map((n) => (
                  <button key={n} type="button" className="admin-panel__chip" onClick={() => setGold(String(n))}>
                    {n.toLocaleString('fr-FR')}
                  </button>
                ))}
              </div>
            </div>
            <div className="admin-panel__btnrow">
              <button
                type="button"
                className="admin-panel__button admin-panel__button--danger"
                disabled={!live}
                title={offlineHint}
                onClick={() =>
                  confirm({
                    title: `Vider l'inventaire de ${player.n || 'ce joueur'} ?`,
                    body: "Tout l'inventaire est détruit, or et équipement compris. Cette action ne peut pas être annulée.",
                    confirmLabel: "Vider l'inventaire",
                    danger: true,
                    onConfirm: () => act('wipeInventory', { target }, 'Inventaire vidé'),
                  })
                }
              >
                Vider l'inventaire
              </button>
            </div>
          </section>
        ) : null}

        {canPlayers ? (
          <section className="admin-panel__group" aria-label="Personnage">
            <h3 className="admin-panel__group-title">Personnage</h3>
            <div className="admin-panel__btnrow">
              {btn("Menu d'apparence", 'openRaceMenu', {}, { pending: "Menu d'apparence" })}
              <button
                type="button"
                className="admin-panel__button"
                onClick={() => act(player.slot2 ? 'revokeExtraSlot' : 'grantExtraSlot', { target: String(player.p) }, 'Emplacement')}
              >
                {player.slot2 ? 'Retirer le 2e emplacement' : 'Accorder un 2e emplacement'}
              </button>
            </div>
          </section>
        ) : null}

        {canModes && modeList.length ? (
          <section className="admin-panel__group" aria-label="Modes pour la cible">
            <h3 className="admin-panel__group-title">Modes pour cette personne</h3>
            <p className="admin-panel__hint">Chaque bouton active ou désactive le mode pour elle. Elle doit être en ligne.</p>
            <div className="admin-panel__chips">
              {modeList.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className="admin-panel__chip"
                  disabled={!live}
                  title={!live ? offlineHint : undefined}
                  onClick={() => act('toggleModeForTarget', { target, mode: m.id }, m.label)}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </aside>
  );
};

export const PlayersView = React.memo(PlayersViewInner);
